import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MapIcon from '@mui/icons-material/Map';
import logo from '../../../assets/fattyBattyLogo.png';
import { environment } from '../../../environments/environment';
import AddressPickerWithGoogle from '../../components/Address/GoogleAutoComplete';
import { useStoreFilter } from '../../hooks/useStoreFilter';
import BranchItem from './BranchItem';
import BranchMapView from './BranchMapView';
import { useUserLocation } from '../../hooks/useUserLocation';
import { FaTruck } from 'react-icons/fa';
import { GiPaperBagOpen } from 'react-icons/gi';
import { useDispatch, useSelector } from 'react-redux';
import { auth, firestore } from '@tabletuck/firebase-services';
import {
  setUser,
  UpdateScheduledTime,
  setStores,
  setOrderTypeLocked,
} from '@tabletuck/redux-slices';
import SchedulingDialog from '../../components/SchedulingDialog/SchedulingDialog';
import { getStoreStatus } from '../../Utils/storeTimingUtils';
import { useDeliveryAddress } from '../../hooks/useDeliveryAddress';
import { toast } from 'react-toastify';
const ChangeBranchMenu = ({
  isOpen,
  onClose,
  onSelectBranch,
  savedDeliveryAddress,
  clearAddressOnOpen = false,
}) => {
  const { isGettingLocation, getUserLocationViaGPS } = useUserLocation({
    autoStart: true,
    enableConsoleLogging: true,
  });

  const [selectedBranch, setSelectedBranch] = useState(null); // Start with null to indicate no selection
  const [userLocation, setUserLocation] = useState(null); // Start with no location to show search input

  const [selectedFilter, setSelectedFilter] = useState('none'); // Default to no filter
  const [showLocationInput, setShowLocationInput] = useState(true); // Start with search input visible
  const dialogContentRef = useRef(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [addressInputValue, setAddressInputValue] = useState('');
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const [showSchedulingDialog, setShowSchedulingDialog] = useState(false);
  const [pendingStoreSelection, setPendingStoreSelection] = useState(null);
  const [userClearedFilters, setUserClearedFilters] = useState(false); // Track if user intentionally cleared filters
  const hasInitializedRef = useRef(false); // Track if we've initialized address for current dialog session
  const isManuallySelectingAddressRef = useRef(false); // Track if user is manually selecting/saving an address
  const lastSavedAddressRef = useRef(null); // Cache the last saved address to persist across dialog sessions

  const dispatch = useDispatch();
  const userData = useSelector((state) => state.authReducer.userData);
  const reduxStores = useSelector((state) => state.shopReducer.stores);
  const [hasLoadedStores, setHasLoadedStores] = useState(false);

  // Use unified delivery address hook for syncing address across components
  const { setDeliveryAddressFromLocation, addresses: savedAddresses } =
    useDeliveryAddress();
  const savedAddressesList = useMemo(
    () =>
      (savedAddresses || []).filter(
        (a) =>
          a?.lat != null &&
          a?.lng != null &&
          !a?.isTemporary &&
          a?.isDisplayed !== false
      ),
    [savedAddresses]
  );

  // Use the store filter hook with a 7 miles radius
  const {
    filteredStores,
    isLoadingStores,
    selectStore,
    updateLocation,
    clearLocationFilter,
    fetchStores,
  } = useStoreFilter({
    enableConsoleLogging: true,
    radiusMiles: 25,
    cachedStores: reduxStores, // Pass cached stores from Redux
    autoFetch: false, // Don't auto-fetch, we'll trigger manually when dialog opens
  });

  // Lazy load stores when dialog opens
  useEffect(() => {
    // Reset initialization flag when dialog closes
    // This allows proper re-initialization when dialog reopens
    if (!isOpen) {
      hasInitializedRef.current = false;
      // Clear userLocation state when dialog closes so we get fresh init from savedDeliveryAddress on reopen
      setUserLocation(null);
      setAddressInputValue('');
      setShowLocationInput(true);
    }

    if (isOpen && !hasLoadedStores) {
      // If we have cached stores in Redux, no need to fetch again
      if (reduxStores && reduxStores.length > 0) {
        setHasLoadedStores(true);
      } else {
        // Fetch stores and cache them in Redux
        fetchStores()
          .then((stores) => {
            if (stores && stores.length > 0) {
              dispatch(setStores(stores));
              setHasLoadedStores(true);
            } else {
              setHasLoadedStores(true); // Mark as loaded even if no stores found
            }
          })
          .catch((error) => {
            // Handle error silently - stores may already be in Redux
            setHasLoadedStores(true);
          });
      }
    }
  }, [isOpen, hasLoadedStores, reduxStores, fetchStores, dispatch]);

  // Initialize userLocation from cached address or savedDeliveryAddress if available
  useEffect(() => {
    // When opening for "branch closed" revalidate: start with no address so user can re-enter
    if (isOpen && clearAddressOnOpen) {
      lastSavedAddressRef.current = null;
      hasInitializedRef.current = true;
      setUserLocation(null);
      setAddressInputValue('');
      setShowLocationInput(true);
      clearLocationFilter();
      return;
    }

    // Prefer lastSavedAddressRef over savedDeliveryAddress prop
    // The cached address is more up-to-date since parent prop may be stale
    const addressToUse = lastSavedAddressRef.current || savedDeliveryAddress;

    // Initialize from addressToUse if:
    // 1. Dialog is open
    // 2. We have an address to use (cached or from prop)
    // 3. User hasn't cleared filters
    // 4. Haven't already initialized for this session
    // 5. Not currently in the middle of a manual address selection
    // 6. Either we don't have userLocation OR address changed (different ID/placeId/sourceAddressId)
    const shouldInitialize =
      isOpen &&
      addressToUse &&
      !userClearedFilters &&
      !hasInitializedRef.current &&
      !isManuallySelectingAddressRef.current &&
      (!userLocation ||
        (userLocation.sourceAddressId !== addressToUse?.id &&
          userLocation.sourceAddressId !== addressToUse?.sourceAddressId &&
          userLocation.placeId !== addressToUse?.placeId));

    if (shouldInitialize) {
      // If using cached address (already in userLocation format), use directly
      // Otherwise convert from saved address format
      const locationFromSaved = lastSavedAddressRef.current || {
        country: addressToUse.country,
        state: addressToUse.state,
        city: addressToUse.city,
        zipCode: addressToUse.zipCode,
        placeId: addressToUse.placeId,
        displayName: addressToUse.displayName || addressToUse.address,
        lat: addressToUse.lat,
        lng: addressToUse.lng,
        streetNumber: addressToUse.streetNumber,
        route: addressToUse.route,
        sourceAddressId:
          addressToUse.sourceAddressId ||
          addressToUse.placeId ||
          addressToUse.id,
      };
      setUserLocation(locationFromSaved);
      setAddressInputValue(locationFromSaved.displayName);
      setShowLocationInput(false);

      // Apply location filter with the saved address
      if (locationFromSaved.lat && locationFromSaved.lng) {
        updateLocation(locationFromSaved);
      }

      hasInitializedRef.current = true;
    } else if (isOpen && userLocation && !hasInitializedRef.current) {
      // Dialog just opened and we already have a userLocation
      // Re-apply the filter if coordinates are available
      const currentDisplayName =
        userLocation.displayName ||
        (userLocation.city
          ? `${userLocation.city}, ${
              userLocation.state || userLocation.country || ''
            }`
              .trim()
              .replace(/,$/, '')
          : '');
      setAddressInputValue(currentDisplayName);

      if (userLocation.lat && userLocation.lng) {
        updateLocation(userLocation);
      }

      hasInitializedRef.current = true;
    }
  }, [
    isOpen,
    clearAddressOnOpen,
    savedDeliveryAddress,
    userLocation,
    userClearedFilters,
    updateLocation,
    clearLocationFilter,
  ]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (locationUpdateTimerRef.current) {
        clearTimeout(locationUpdateTimerRef.current);
      }
    };
  }, []);

  // Use filtered stores for display with service type filtering
  const storesToDisplay = useMemo(() => {
    if (selectedFilter === 'none') {
      return filteredStores; // Show all stores when no filter is selected
    }

    return filteredStores.filter((store) => {
      if (selectedFilter === 'pickup') {
        return store.pickup_enabled === true;
      }
      if (selectedFilter === 'delivery') {
        const withinDeliveryRadius =
          store.distance == null || store.distance <= 5;
        return store.delivery_enabled === true && withinDeliveryRadius;
      }
      return true; // Fallback to show all
    });
  }, [filteredStores, selectedFilter]);

  // Select the first store by default when stores load for the first time
  useEffect(() => {
    if (storesToDisplay.length > 0 && selectedBranch === null) {
      setSelectedBranch(0);
    }
  }, [storesToDisplay, selectedBranch]);

  // Only reset to first store when stores change if no manual selection was made
  // or if current selection is out of bounds
  useEffect(() => {
    if (
      storesToDisplay.length > 0 &&
      (selectedBranch === null || selectedBranch >= storesToDisplay.length)
    ) {
      setSelectedBranch(0);
    }
  }, [storesToDisplay, selectedBranch]);

  // Remove the effect that sets userLocation from initialUserLocation
  // since we want to start with empty location for user input
  // Debounce timer ref for location updates
  const locationUpdateTimerRef = useRef(null);

  // Handle Google address selection and apply location filter with debouncing
  // Also syncs the address as delivery address when in delivery mode
  const getGoogleAddress = useCallback(
    (addressData) => {
      if (!addressData || !addressData.lat || !addressData.lng) {
        return;
      }

      if (addressData && addressData.lat && addressData.lng) {
        isManuallySelectingAddressRef.current = true;

        const newLocation = {
          country: addressData.country,
          state: addressData.state,
          city: addressData.city,
          zipCode: addressData.zipCode,
          placeId: addressData.placeId,
          displayName: addressData.displayName,
          lat: addressData.lat,
          lng: addressData.lng,
          streetNumber: addressData.streetNumber,
          route: addressData.route,
          sourceAddressId: addressData.placeId,
        };
        setUserClearedFilters(false);
        setUserLocation(newLocation);

        if (locationUpdateTimerRef.current) {
          clearTimeout(locationUpdateTimerRef.current);
        }
        locationUpdateTimerRef.current = setTimeout(async () => {
          try {
            updateLocation(newLocation);
            setShowLocationInput(false);

            await setDeliveryAddressFromLocation(newLocation, '', {
              saveToFirestore: true,
              setAsDefault: true,
              source: 'branchMenu',
              isDisplayed: false,
            });
            lastSavedAddressRef.current = newLocation;
            setTimeout(() => {
              isManuallySelectingAddressRef.current = false;
            }, 500);
          } catch (error) {
            isManuallySelectingAddressRef.current = false;
          }
        }, 300);
      }
    },
    [updateLocation, setDeliveryAddressFromLocation]
  );

  const handleSelectSavedAddress = useCallback(
    (addr) => {
      if (addr?.lat == null || addr?.lng == null) return;
      const addressData = {
        country: addr.country ?? '',
        state: addr.state ?? '',
        city: addr.city ?? '',
        zipCode: addr.zipCode ?? '',
        placeId: addr.placeId ?? addr.id,
        displayName: addr.displayName || addr.address || '',
        lat: addr.lat,
        lng: addr.lng,
        streetNumber: addr.streetNumber ?? '',
        route: addr.route ?? '',
      };
      getGoogleAddress(addressData);
    },
    [getGoogleAddress]
  );

  const openLocationInputAndScrollTop = () => {
    setShowLocationInput(true);
    try {
      const el = dialogContentRef?.current;
      if (el) {
        if (typeof el.scrollTo === 'function')
          el.scrollTo({ top: 0, behavior: 'smooth' });
        else el.scrollTop = 0;
      }
    } catch (e) {
      // ignore
    }
  };

  // Handle using current GPS location - GPS only for accurate address when user clicks the button
  const handleUseCurrentLocation = useCallback(async () => {
    isManuallySelectingAddressRef.current = true;
    try {
      setIsResolvingLocation(true);
      let currentLocation;
      try {
        currentLocation = await getUserLocationViaGPS({ highAccuracy: true });
      } catch (err) {
        const msg = err?.message ?? '';
        if (msg.toLowerCase().includes('denied')) {
          toast.warning('Access to your location is denied. Please enable it in your browser settings.');
        } else {
          toast.error(
            'Could not get your location. Try again or enter your address.'
          );
        }
        setIsResolvingLocation(false);
        isManuallySelectingAddressRef.current = false;
        return;
      }

      if (currentLocation && currentLocation.lat && currentLocation.lng) {
        // Try reverse geocoding to get a human-readable address
        try {
          const lat = currentLocation.lat;
          const lng = currentLocation.lng;
          let displayName = '';

          if (environment.googleMapsApiKey) {
            // Use Google Geocoding API; pick most accurate result by location_type
            const resp = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${environment.googleMapsApiKey}`
            );
            const data = await resp.json();
            if (data && data.results && data.results.length > 0) {
              const precisionRank = {
                ROOFTOP: 0,
                RANGE_INTERPOLATED: 1,
                GEOMETRIC_CENTER: 2,
                APPROXIMATE: 3,
              };
              const getRank = (r) =>
                precisionRank[r.geometry?.location_type] ?? 3;
              const best = data.results.reduce(
                (acc, r) => (getRank(r) < getRank(acc) ? r : acc),
                data.results[0]
              );
              displayName = best.formatted_address;
            }
          }

          // Fallback to Nominatim if no Google key or no results
          if (!displayName) {
            try {
              const resp2 = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
              );
              const d2 = await resp2.json();
              if (d2 && d2.display_name) displayName = d2.display_name;
            } catch (e) {
              // Reverse geocode fallback failed
            }
          }

          const enrichedLocation = {
            ...currentLocation,
            displayName:
              displayName || `Lat ${lat.toFixed(4)}, Lng ${lng.toFixed(4)}`,
            sourceAddressId: currentLocation.placeId,
          };

          setAddressInputValue(enrichedLocation.displayName);
          setUserClearedFilters(false);
          setUserLocation(enrichedLocation);
          updateLocation(enrichedLocation);
          setShowLocationInput(false);

          try {
            await setDeliveryAddressFromLocation(enrichedLocation, '', {
              saveToFirestore: true,
              setAsDefault: true,
              source: 'branchMenu',
              isDisplayed: false,
            });
            // Cache the saved address so it persists across dialog sessions
            lastSavedAddressRef.current = enrichedLocation;
            setTimeout(() => {
              isManuallySelectingAddressRef.current = false;
            }, 500);
          } catch (error) {
            isManuallySelectingAddressRef.current = false;
          }
        } catch (err) {
          setUserClearedFilters(false);
          const fallbackLocation = {
            ...currentLocation,
            sourceAddressId: currentLocation.placeId,
          };
          setUserLocation(fallbackLocation);
          updateLocation(fallbackLocation);
          setShowLocationInput(false);

          try {
            await setDeliveryAddressFromLocation(fallbackLocation, '', {
              saveToFirestore: true,
              setAsDefault: true,
              source: 'branchMenu',
              isDisplayed: false,
            });
            // Cache the saved address so it persists across dialog sessions
            lastSavedAddressRef.current = fallbackLocation;
            setTimeout(() => {
              isManuallySelectingAddressRef.current = false;
            }, 500);
          } catch (error) {
            isManuallySelectingAddressRef.current = false;
          }
        }
      }
      setIsResolvingLocation(false);
    } catch (error) {
      setIsResolvingLocation(false);
      isManuallySelectingAddressRef.current = false;
    }
  }, [getUserLocationViaGPS, updateLocation, setDeliveryAddressFromLocation]);

  const handleBranchSelect = (index) => {
    setSelectedBranch(index);

    // Also select the store in the hook so it becomes the active store
    const selectedStore = storesToDisplay[index];
    if (selectedStore) {
      selectStore(selectedStore);
      // Update user's pickupOrDelivery preference and defaultStore
      // If a service filter is active and the store supports both, respect the filter
      let newType = 'pickup';
      if (selectedFilter === 'delivery' && selectedStore?.delivery_enabled) {
        newType = 'delivery';
      } else if (selectedFilter === 'pickup' && selectedStore?.pickup_enabled) {
        newType = 'pickup';
      } else {
        // fallback to store capabilities
        if (selectedStore?.delivery_enabled && !selectedStore?.pickup_enabled) {
          newType = 'delivery';
        } else if (
          !selectedStore?.delivery_enabled &&
          selectedStore?.pickup_enabled
        ) {
          newType = 'pickup';
        } else {
          newType = 'pickup';
        }
      }

      const updatedUser = {
        ...(userData || {}),
        pickupOrDelivery: newType,
        defaultStore: selectedStore.id,
      };
      dispatch(setUser({ userData: updatedUser }));
      dispatch(setOrderTypeLocked(newType));
      if (auth?.currentUser?.uid) {
        const uid = auth.currentUser.uid;
        firestore
          .collection('users')
          .doc(uid)
          .set(
            { pickupOrDelivery: newType, defaultStore: selectedStore.id },
            { merge: true }
          )
          .then(async () => {
            try {
              const doc = await firestore.collection('users').doc(uid).get();
              const fresh = doc.data();
              // fetched fresh user doc after persist
              dispatch(setUser({ userData: fresh }));
            } catch (e) {
              // Failed to fetch user doc after persist
            }
          })
          .catch((e) => {
            // Failed to persist pickupOrDelivery
          });
      }
    }
  };

  // Centralized finalize handler used by BranchItem's Start Order button
  const finalizeSelection = async (index) => {
    // mark selection in UI
    setSelectedBranch(index);
    const selectedStoreData = storesToDisplay[index];

    if (selectedStoreData) {
      selectStore(selectedStoreData);

      // Check if store is not available for online ordering
      const isNotAvailableOnline =
        !selectedStoreData?.pickup_enabled &&
        !selectedStoreData?.delivery_enabled;

      // If user filtered by a specific service type and the store supports both,
      // prefer the user's filter choice. Otherwise, derive from store capabilities.
      let newType = 'pickup';
      if (
        selectedFilter === 'delivery' &&
        selectedStoreData?.delivery_enabled
      ) {
        newType = 'delivery';
      } else if (
        selectedFilter === 'pickup' &&
        selectedStoreData?.pickup_enabled
      ) {
        newType = 'pickup';
      } else {
        if (
          selectedStoreData?.delivery_enabled &&
          !selectedStoreData?.pickup_enabled
        ) {
          newType = 'delivery';
        } else if (
          !selectedStoreData?.delivery_enabled &&
          selectedStoreData?.pickup_enabled
        ) {
          newType = 'pickup';
        } else {
          newType = 'pickup';
        }
      }

      // SYNC: Set userLocation as delivery address for this session (do not save to Firebase saved addresses)
      if (userLocation) {
        try {
          await setDeliveryAddressFromLocation(
            userLocation,
            selectedStoreData.id,
            {
              saveToFirestore: true,
              setAsDefault: true,
              source: 'branchMenu',
              isDisplayed: false,
            }
          );
        } catch (error) {
          // Handle error silently
        }
      }

      // If store is not available for online ordering, just proceed without scheduling
      if (isNotAvailableOnline) {
        // Clear any existing scheduled time since store doesn't support online ordering
        dispatch(UpdateScheduledTime(null));

        const updatedUser = {
          ...(userData || {}),
          pickupOrDelivery: newType,
          defaultStore: selectedStoreData.id,
        };

        dispatch(setUser({ userData: updatedUser }));

        if (auth?.currentUser?.uid) {
          const uid = auth.currentUser.uid;

          firestore
            .collection('users')
            .doc(uid)
            .set(
              { pickupOrDelivery: newType, defaultStore: selectedStoreData.id },
              { merge: true }
            )
            .then(async () => {
              try {
                const doc = await firestore.collection('users').doc(uid).get();
                const fresh = doc.data();
                dispatch(setUser({ userData: fresh }));
              } catch (e) {
                // Failed to fetch user doc after persist
              }
            })
            .catch((e) => {
              // Failed to persist
            });
        }

        onSelectBranch(selectedStoreData, userLocation, selectedFilter);
        onClose();
        return;
      }

      // Show scheduling dialog when store is available for online ordering
      // (both when branch is open and closed - allow scheduling in both cases)
      const storeStatus = getStoreStatus(selectedStoreData);
      const pendingSelection = {
        index,
        storeData: selectedStoreData,
        orderType: newType,
        userLocation,
        serviceFilter: selectedFilter,
        isStoreOpen: storeStatus.isOpen,
      };

      setPendingStoreSelection(pendingSelection);
      setShowSchedulingDialog(true);
      return;
    }
    onClose();
  };

  const handleSchedulingConfirm = (schedulingData) => {
    if (!pendingStoreSelection) {
      return;
    }

    const { storeData, orderType, userLocation, serviceFilter } =
      pendingStoreSelection;

    // Call parent callback FIRST to update selectedStore in MenuItems2
    // This ensures selectedStore.id matches the storeId in schedulingData
    // Pass preserveScheduledTime=true to prevent handleStoreSelected from clearing
    // the scheduled time that we're about to set via Redux dispatch
    onSelectBranch(storeData, userLocation, serviceFilter, true);

    // Update user preferences
    const updatedUser = {
      ...(userData || {}),
      pickupOrDelivery: orderType,
      defaultStore: storeData.id,
    };

    dispatch(setUser({ userData: updatedUser }));
    dispatch(setOrderTypeLocked(orderType));

    // ASAP: store a marker so ConfirmOrderPage can pre-select ASAP
    // Scheduled: persist the chosen time slot
    if (schedulingData.isAsap) {
      dispatch(UpdateScheduledTime({ isAsap: true, dateOfOrder: null, storeId: storeData.id, displayTime: schedulingData.displayTime }));
    } else {
      const schedulingPayload = { ...schedulingData, storeId: storeData.id };
      dispatch(UpdateScheduledTime(schedulingPayload));
    }

    if (auth?.currentUser?.uid) {
      const uid = auth.currentUser.uid;

      firestore
        .collection('users')
        .doc(uid)
        .set(
          { pickupOrDelivery: orderType, defaultStore: storeData.id },
          { merge: true }
        )
        .then(async () => {
          try {
            const doc = await firestore.collection('users').doc(uid).get();
            const fresh = doc.data();
            dispatch(setUser({ userData: fresh }));
          } catch (e) {
            // Failed to fetch user doc after scheduling
          }
        })
        .catch((e) => {
          // Failed to persist after scheduling
        });
    }

    // Close scheduling dialog
    setShowSchedulingDialog(false);
    setPendingStoreSelection(null);

    // Close main dialog
    onClose();
  };

  const handleSchedulingClose = () => {
    setShowSchedulingDialog(false);
    setPendingStoreSelection(null);
  };

  // const handleSelect = () => {
  //   const branchIndex = selectedBranch !== null ? selectedBranch : 0; // Fallback to first store if none selected
  //   const selectedStoreData = storesToDisplay[branchIndex];
  //   if (selectedStoreData) {
  //     const newType =
  //       selectedStoreData?.delivery_enabled && selectedStoreData?.pickup_enabled
  //         ? 'pickup'
  //         : selectedStoreData?.delivery_enabled
  //         ? 'delivery'
  //         : selectedStoreData?.pickup_enabled
  //         ? 'pickup'
  //         : 'pickup';

  //     const updatedUser = {
  //       ...(userData || {}),
  //       pickupOrDelivery: newType,
  //       defaultStore: selectedStoreData.id,
  //     };
  //     dispatch(setUser({ userData: updatedUser }));
  //     if (auth?.currentUser?.uid) {
  //       const uid = auth.currentUser.uid;
  //       firestore
  //         .collection('users')
  //         .doc(uid)
  //         .set(
  //           { pickupOrDelivery: newType, defaultStore: selectedStoreData.id },
  //           { merge: true }
  //         )
  //         .then(async () => {
  //           try {
  //             const doc = await firestore.collection('users').doc(uid).get();
  //             const fresh = doc.data();
  //             // fetched fresh user doc after persist (handleSelect)
  //             dispatch(setUser({ userData: fresh }));
  //           } catch (e) {
  //             console.warn(
  //               '[ChangeBranchMenu] failed to fetch user doc after persist (handleSelect)',
  //               e
  //             );
  //           }
  //         })
  //         .catch((e) =>
  //           console.warn(
  //             'Failed to persist pickupOrDelivery (handleSelect):',
  //             e
  //           )
  //         );
  //     }

  //     onSelectBranch(selectedStoreData, userLocation, selectedFilter);
  //   }
  //   onClose();
  // };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '570px',

          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        },
      }}
    >
      <DialogContent ref={dialogContentRef} sx={{ p: 0, position: 'relative' }}>
        {/* Header with Logo */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <img
              src={logo}
              alt="logo"
              style={{ width: '70px', height: '70px' }}
            />
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: '#6c757d',
              '&:hover': {
                backgroundColor: 'rgba(108, 117, 125, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Current Location Section */}
        <Box sx={{ px: 3, mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#4F4F4F',
              mb: 1,
              fontSize: '16px',
              fontFamily: 'Jost',
            }}
          >
            Your Current Location
          </Typography>

          {!showLocationInput ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                backgroundColor: '#DDDDDD1C',
                borderRadius: '12px',
                border: '1px solid #DBDBDB',
                height: '60px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: environment.mainColor,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${environment.mainColor}26`,
                },
              }}
              onClick={openLocationInputAndScrollTop}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  backgroundColor: environment.mainColor,
                  borderRadius: '50%',
                  flexShrink: 0,
                }}
              >
                <LocationIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#172B4D',
                    fontWeight: 600,
                    fontFamily: 'Jost',
                    fontSize: { xs: '12px', sm: '16px' },
                    // Prevent long addresses from overflowing on small screens
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    // Constrain width on very small screens to keep the layout intact
                    maxWidth: { xs: '180px', sm: '100%' },
                  }}
                >
                  {typeof userLocation?.displayName === 'string'
                    ? userLocation.displayName
                    : userLocation?.city
                    ? `${userLocation.city}, ${
                        userLocation.state || userLocation.country || ''
                      }`
                        .trim()
                        .replace(/,$/, '')
                    : ' Enter a location to find nearby restaurants'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6B7280',
                    fontSize: { xs: '10px', sm: '12px' },
                    fontFamily: 'Jost',
                  }}
                >
                  Tap to change location
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  backgroundColor: `${environment.mainColor}1A`,
                  borderRadius: '50%',
                  flexShrink: 0,
                }}
              >
                <EditIcon sx={{ color: environment.mainColor, fontSize: 16 }} />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: 3,
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                border: `2px solid ${environment.mainColor}`,
                // boxShadow: `0 8px 24px ${environment.mainColor}26`,
                transition: 'all 0.3s ease',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ color: environment.mainColor, mr: 1 }} />
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: '#172B4D',
                    fontFamily: 'Jost',
                  }}
                >
                  Enter your address
                </Typography>
              </Box>

              <AddressPickerWithGoogle
                getValues={getGoogleAddress}
                // Prefill with resolved address (from GPS reverse-geocode) or existing userLocation
                initialValue={
                  addressInputValue || userLocation?.displayName || ''
                }
                // Let the autocomplete show a spinner while we're resolving the GPS -> address
                externalLoading={isResolvingLocation}
              />

              {savedAddressesList.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: '#6B7280',
                      fontWeight: 600,
                      mb: 1,
                      fontFamily: 'Jost',
                    }}
                  >
                    Saved addresses
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      maxHeight: 140,
                      overflowY: 'auto',
                    }}
                  >
                    {savedAddressesList.map((addr) => (
                      <Box
                        key={addr.id}
                        onClick={() => handleSelectSavedAddress(addr)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectSavedAddress(addr);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1.25,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(0,0,0,0.03)',
                          transition: 'background-color 0.15s',
                          '&:hover': {
                            backgroundColor: `${environment.mainColor}18`,
                          },
                          '&:focus-visible': {
                            outline: `2px solid ${environment.mainColor}`,
                            outlineOffset: 2,
                          },
                        }}
                      >
                        <LocationIcon
                          sx={{
                            color: environment.mainColor,
                            fontSize: 18,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#172B4D',
                            fontFamily: 'Jost',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {addr.displayName || addr.address || 'Address'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  size="small"
                  onClick={() => {
                    // Cancel: only close the address edit input, keep address & filters
                    setShowLocationInput(false);
                  }}
                  sx={{
                    color: '#6B7280',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(107, 114, 128, 0.1)',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

          {/* Use Current Location Button */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<LocationIcon />}
              onClick={handleUseCurrentLocation}
              disabled={isGettingLocation || isResolvingLocation}
              sx={{
                borderColor: environment.mainColor,
                color: environment.mainColor,
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: '25px',
                px: 3,
                py: 1,
                fontSize: '14px',
                fontFamily: 'Jost',
                '&:hover': {
                  backgroundColor: `${environment.mainColor}10`,
                  borderColor: environment.mainColor,
                },
                '&:disabled': {
                  borderColor: '#ccc',
                  color: '#999',
                },
              }}
            >
              {isGettingLocation || isResolvingLocation ? (
                <>
                  <CircularProgress
                    size={16}
                    sx={{ color: environment.mainColor, mr: 1 }}
                  />
                  Resolving...
                </>
              ) : (
                'Use My Current Location'
              )}
            </Button>
          </Box>

          {/* Clear / Show All Buttons */}
          {(userLocation || addressInputValue || selectedFilter !== 'none') && (
            <Box
              sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'center' }}
            >
              {selectedFilter === 'delivery' && (
                <Button
                  variant="text"
                  onClick={() => {
                    clearLocationFilter();
                    hasInitializedRef.current = false;
                    setSelectedFilter('pickup');
                  }}
                  sx={{
                    color: '#6B7280',
                    fontWeight: 400,
                    textTransform: 'none',
                    fontSize: '12px',
                    fontFamily: 'Jost',
                    '&:hover': {
                      backgroundColor: 'rgba(107, 114, 128, 0.1)',
                    },
                  }}
                >
                  Show All Locations
                </Button>
              )}

              <Button
                variant="text"
                onClick={() => {
                  // Clear filters and restore modal to initial state
                  setAddressInputValue('');
                  setUserLocation(null);
                  setSelectedFilter('none');
                  setSelectedBranch(null);
                  setUserClearedFilters(true); // Mark that user intentionally cleared filters
                  lastSavedAddressRef.current = null; // Clear cached address
                  clearLocationFilter(false);
                  setShowLocationInput(true); // Show input so user can enter new address
                }}
                aria-label="Clear filters"
                sx={{
                  color: '#6B7280',
                  fontWeight: 400,
                  textTransform: 'none',
                  fontSize: '12px',
                  fontFamily: 'Jost',
                  '&:hover': {
                    backgroundColor: 'rgba(107, 114, 128, 0.1)',
                  },
                }}
              >
                Clear Filters
              </Button>
            </Box>
          )}
        </Box>

        {/* Service Type Filter Section */}
        <Box sx={{ px: 3, mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#4F4F4F',
              mb: 2,
              fontSize: '16px',
              fontFamily: 'Jost',
            }}
          >
            Service Type
          </Typography>
 {selectedFilter === 'none' && (
            <Typography
              variant="body2"
              sx={{
                my: 1.5,
                textAlign: 'start',
                color: '#6B7280',
                fontFamily: 'Jost',
                fontSize: '13px',
              }}
            >
              Select service type to show list of locations.
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              backgroundColor: '#f8f9fa',
              borderRadius: '16px',
              p: 1,
              border: '1px solid #e9ecef',
            }}
          >
            {[
              {
                key: 'pickup',
                label: 'Pickup',
                icon: <GiPaperBagOpen size={20} />,
              },
              {
                key: 'delivery',
                label: 'Delivery',
                icon: <FaTruck size={20} />,
              },
              // {
              //   key: 'both',
              //   label: 'Pickup & Delivery',
              //   icon: (
              //     <span
              //       style={{ display: 'flex', gap: 6, alignItems: 'center' }}
              //     >
              //       <GiPaperBagOpen size={20} />
              //       <FaTruck size={20} />
              //     </span>
              //   ),
              // },
            ].map((option) => (
              <Button
                key={option.key}
                onClick={() => setSelectedFilter(option.key)}
                sx={{
                  flex: 1,
                  py: 1.5,
                  px: 2,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontFamily: 'Jost',
                  fontWeight: 600,
                  fontSize: '14px',
                  transition: 'all 0.3s ease',
                  backgroundColor:
                    selectedFilter === option.key
                      ? environment.mainColor
                      : 'transparent',
                  color: selectedFilter === option.key ? '#ffffff' : '#6B7280',
                  border: 'none',
                  boxShadow:
                    selectedFilter === option.key
                      ? `0 4px 12px ${environment.mainColor}4D`
                      : 'none',
                  transform:
                    selectedFilter === option.key ? 'translateY(-1px)' : 'none',
                  '&:hover': {
                    backgroundColor:
                      selectedFilter === option.key
                        ? environment.mainColor
                        : `${environment.mainColor}1A`,
                    color:
                      selectedFilter === option.key
                        ? '#ffffff'
                        : environment.mainColor,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${environment.mainColor}33`,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 16,
                  }}
                >
                  {option.icon}
                </span>
                {option.label}
              </Button>
            ))}
          </Box>

         
        </Box>

        {/* Restaurant Selection Section */}
        {selectedFilter !== 'none' && <Box
          sx={{
            px: 3,
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            // height: '400px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#4F4F4F',
                fontSize: '16px',
                fontFamily: 'Jost',
              }}
            >
              Select Restaurant Location
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: '#fff',
                borderRadius: '999px',
                p: '4px',
                boxShadow: 'inset 0 0 0 1px rgba(16,24,40,0.03)',
              }}
            >
              <Button
                onClick={() => setViewMode('list')}
                startIcon={<ListAltIcon />}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: '999px',
                  px: 2,
                  py: 0.5,
                  minWidth: 90,
                  mr: 0.5,
                  backgroundColor:
                    viewMode === 'list' ? environment.mainColor : 'transparent',
                  color: viewMode === 'list' ? '#fff' : '#374151',
                  boxShadow:
                    viewMode === 'list'
                      ? '0 6px 18px -6px rgba(0,0,0,0.2)'
                      : 'none',
                  '&:hover': {
                    backgroundColor:
                      viewMode === 'list' ? environment.mainColor : '#f3f4f6',
                  },
                }}
              >
                List
              </Button>

              <Button
                onClick={() => setViewMode('map')}
                startIcon={<MapIcon />}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: '999px',
                  px: 2,
                  py: 0.5,
                  minWidth: 90,
                  backgroundColor:
                    viewMode === 'map' ? environment.mainColor : 'transparent',
                  color: viewMode === 'map' ? '#fff' : '#374151',
                  boxShadow:
                    viewMode === 'map'
                      ? '0 6px 18px -6px rgba(0,0,0,0.2)'
                      : 'none',
                  '&:hover': {
                    backgroundColor:
                      viewMode === 'map' ? environment.mainColor : '#f3f4f6',
                  },
                }}
              >
                Map
              </Button>
            </Box>
          </Box>

          {/* Scrollable Cards Container */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: 1,
              mb: 3,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '8px',
              },
            }}
          >
            {isLoadingStores ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : storesToDisplay.length === 0 ? (
              // Empty state when no branches match the current filters/location
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <LocationIcon
                  sx={{ fontSize: 48, color: environment.mainColor, mb: 2 }}
                />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: '#172B4D', mb: 1 }}
                >
                  No branches found
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
                  We couldn't find any restaurant locations near the selected
                  address or filters.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => openLocationInputAndScrollTop()}
                    startIcon={<EditIcon />}
                    aria-label="Change location"
                    sx={{
                      background: environment.mainColor,
                      color: '#fff',
                      textTransform: 'none',
                      borderRadius: '999px',
                      px: 3,
                      py: 1,

                      fontWeight: 600,
                    }}
                  >
                    Change location
                  </Button>

                  {selectedFilter !== 'delivery' && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        // Clear the location filter but keep the selected address
                        // visible in the input. Open the input so user can edit.
                        clearLocationFilter();
                        openLocationInputAndScrollTop();
                      }}
                      startIcon={<LocationIcon />}
                      aria-label="Show all locations"
                      sx={{
                        textTransform: 'none',
                        borderRadius: '999px',
                        borderColor: '#d1d5db',
                        px: 3,
                        py: 1,
                        color: '#374151',
                        '&:hover': {
                          backgroundColor: '#f7f7f7',
                          borderColor: environment.mainColor,
                          color: environment.mainColor,
                        },
                        fontWeight: 600,
                      }}
                    >
                      Show all
                    </Button>
                  )}
                </Box>
              </Box>
            ) : viewMode === 'list' ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 4, md: 3 },
                }}
              >
                {storesToDisplay.map((store, index) => (
                  <BranchItem
                    key={store.id}
                    store={store}
                    index={index}
                    selectedBranch={selectedBranch}
                    handleBranchSelect={handleBranchSelect}
                    onStartOrder={() => finalizeSelection(index)}
                  />
                ))}
              </Box>
            ) : (
              // Map view only rendered when there are stores to show
              <BranchMapView
                stores={storesToDisplay}
                userLocation={userLocation}
                selectedBranch={selectedBranch}
                onBranchSelect={handleBranchSelect}
                onFinalizeSelection={finalizeSelection}
                isLoading={isLoadingStores}
              />
            )}
          </Box>
        </Box>}

        {/* Fixed Select Button */}
        {/* <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fff',
          }}
        >
          <Button
            variant="contained"
            fullWidth
            onClick={handleSelect}
            sx={{
              background: environment.mainColor,
              color: '#ffffff',
              fontWeight: 600,
              fontSize: '1rem',
              width: '121px',
              p: '9px 16px',
              borderRadius: '52px',
              textTransform: 'none',
              boxShadow: `0 8px 20px ${environment.mainColor}4D`,
              '&:hover': {
                background: environment.mainColor,
                opacity: 0.9,
                boxShadow: `0 6px 16px ${environment.mainColor}66`,
              },
              transition: 'all 0.3s ease',
            }}
          >
            Select
          </Button>
        </Box> */}
      </DialogContent>

      {/* Scheduling Dialog for closed stores */}
      <SchedulingDialog
        open={showSchedulingDialog}
        onClose={handleSchedulingClose}
        onConfirm={handleSchedulingConfirm}
        store={pendingStoreSelection?.storeData}
        orderType={pendingStoreSelection?.orderType || 'pickup'}
        isStoreOpen={pendingStoreSelection?.isStoreOpen}
      />
    </Dialog>
  );
};

export default ChangeBranchMenu;
